


import {
  defineComponent,
  reactive,
  computed,
  onMounted,
  onUnmounted,
  ref,
} from '@vue/composition-api';
import {
  Notification,
  NotificationFile,
} from '@/models/apis/notification/notificationResponse';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { Route } from 'vue-router';
import { UserState } from '@/store/modules/user';
import { dtFormat } from '@/lib/dateHelper';
import { getRedirectDefaultPageUrl } from '@/lib/defaultPageHelper';
import {
  fetchFileAsObjectInfo,
  fileTypeIconFromFilePath,
  isImageFileType,
} from '@/lib/fileHelper';
import { downloadObjectUrl } from '@/lib/downloadHelper';
import { formatUrlsInText } from '@/lib/utils';
import { arrayChunk } from '@/lib/arrayUtil';
import { ModalPhoto, PhotoModalState } from '@/models/photoModal';
import { sortNotifications } from '@/models/notification';
import PhotoModal from '@/components/lib/PhotoModal/index.vue';
import notificationsApi from '@/apis/notifications';
import { LocalStorageActionTypes, LocalStorageGetterTypes } from '@/store/modules/localStorage';
import type { NotificationLocalStorage } from '@/models/notification';

const DEFAULT_REFRESH_INTERVAL_MS = 60000;

interface NotificationAreaState {
  isOpen: boolean;
  isLoading: boolean;
  imageUrls: Record<number, string>;
  showDetailModal: boolean;
  selectedNotification: Notification | null;
}

export default defineComponent({
  components: {
    PhotoModal,
  },
  props: {
    autoRefresh: {
      type: Boolean,
      default: true,
    },
    refreshInterval: {
      type: Number,
      default: DEFAULT_REFRESH_INTERVAL_MS,
    },
    containerClass: {
      type: String,
      default: 'notification-area-container',
    },
  },

  setup(props) {
    const notifications = ref<Notification[]>([]);
    const intervalId = ref<number | null>(null);
    const store = useStore();
    const { route } = useRoute();
    const state = reactive<NotificationAreaState>({
      isOpen: true,
      isLoading: false,
      imageUrls: {},
      showDetailModal: false,
      selectedNotification: null,
    });
    const photoModalState = reactive<PhotoModalState>({
      shouldShowPhotoModal: false,
      title: '',
      photos: [],
    });

    const toggleButtonTitle = computed(() =>
      state.isOpen ? '閉じる' : '開く',
    );
    const toggleButtonIcon = computed(() =>
      state.isOpen ? 'fa-chevron-up' : 'fa-chevron-down',
    );
    const hasNotifications = computed<boolean>(() => {
      return notifications.value && notifications.value.length > 0;
    });
    const shouldShowArea = computed<boolean>(() => {
      const userState = store.state.user;

      if (!userState || userState.id === -1) return false;

      const isCurrentDefaultPage = isDefaultPage(route.value, userState);
      return isCurrentDefaultPage && hasNotifications.value;
    });
    const containerClasses = computed(() => {
      return {
        [props.containerClass]: true,
        'has-margin': shouldShowArea.value,
      };
    });
    const filesByNotification = computed(() => {
      const imageMap = new Map<number, NotificationFile[]>();
      const nonImageMap = new Map<number, NotificationFile[]>();

      notifications.value.forEach(notification => {
        const images: NotificationFile[] = [];
        const nonImages: NotificationFile[] = [];
        (notification.files || []).forEach(file => {
          if (isImageFileType(file.file_path)) {
            images.push(file);
          } else {
            nonImages.push(file);
          }
        });
        imageMap.set(notification.id, images);
        nonImageMap.set(notification.id, nonImages);
      });

      return { imageMap, nonImageMap };
    });
    const imageFilesByNotification = computed(() => filesByNotification.value.imageMap);
    const nonImageFilesByNotification = computed(() => filesByNotification.value.nonImageMap);
    const localStorageKey = LocalStorageGetterTypes.NOTIFICATION_STORAGE;

    const isDefaultPage = (route: Route, userState: UserState): boolean => {
      const defaultPageUrl = getRedirectDefaultPageUrl({
        useJohaisetsuOnly: userState.use_johaisetsu_only,
        useCleaningOnly: userState.use_cleaning_only,
        defaultPage: userState.default_page,
      });
      return route.path === defaultPageUrl;
    };
    const fetchNotifications = async(): Promise<void> => {
      state.isLoading = true;
      try {
        const response = await notificationsApi.index();
        const reNotifications = sortNotifications(response.data);
        const first10Notifications = reNotifications.slice(0, 10);
        notifications.value = first10Notifications;
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        state.isLoading = false;
      }
    };
    const getLastClosedTime = (): Date | null => {
      const localStorage = store.getters[localStorageKey];
      if (!localStorage) return null;

      return new Date(localStorage.notificationAreaLastClosedAt);
    };
    const setLastClosedTime = (): void => {
      const localStorage: NotificationLocalStorage = {
        notificationAreaLastClosedAt: new Date().toISOString(),
      };
      store.dispatch(LocalStorageActionTypes.SET, { key: localStorageKey, val: localStorage });
    };
    const clearLastClosedTime = (): void => {
      store.dispatch(LocalStorageActionTypes.SET, { key: localStorageKey, val: null });
    };
    const shouldForceOpen = (): boolean => {
      if (!hasNotifications.value) return false;

      const lastClosedAt = getLastClosedTime();
      if (!lastClosedAt) return true;

      return notifications.value.some(notification => {
        const publishStartAt = new Date(notification.publish_start_at);
        return publishStartAt > lastClosedAt;
      });
    };
    const toggleArea = (): void => {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        clearLastClosedTime();
        return;
      }

      setLastClosedTime();
    };
    const refreshNotifications = async(): Promise<void> => {
      if (state.isLoading) return;

      await fetchNotifications();
      await preloadImages();
    };
    const viewNotificationDetails = (notification: Notification): void => {
      if (!notification) return;

      state.selectedNotification = notification;
      state.showDetailModal = true;
    };
    const closeDetailModal = (): void => {
      state.showDetailModal = false;
      state.selectedNotification = null;
    };
    const startAutoRefresh = (): void => {
      if (props.autoRefresh && props.refreshInterval > 0) {
        intervalId.value = window.setInterval(() => {
          refreshNotifications();
        }, props.refreshInterval);
      }
    };
    const stopAutoRefresh = (): void => {
      if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }
    };
    const formatDate = (dateString: string): string => {
      if (!dateString) return '';

      const date = new Date(dateString);
      return dtFormat(date, 'yyyy-mm-dd HH:MM');
    };
    const downloadFile = async(file: NotificationFile): Promise<void> => {
      try {
        const fileUrl = await fetchFileObjectUrl(file);
        if (fileUrl) downloadObjectUrl(fileUrl, file.file_name);
      } catch (error) {
        console.error('File download error:', error);
      }
    };
    const getImageFileCount = (notificationId: number): number => {
      return imageFilesByNotification.value.get(notificationId)?.length || 0;
    };
    const previewImageFile = async(notification: Notification): Promise<void> => {
      const imageFile = getFirstImageFile(notification);
      if (!imageFile) return;
      const currentNotification = notifications.value.find(n =>
        n.files?.some(f => f.id === imageFile.id),
      );
      if (!currentNotification) return;

      const photos = await createPhotoModalData(currentNotification);
      photoModalState.title = currentNotification.title;
      photoModalState.photos = photos;
      photoModalState.shouldShowPhotoModal = true;
    };
    const createPhotoModalData = async(notification: Notification): Promise<ModalPhoto[]> => {
      if (!notification.files) return [];

      const imageFiles = imageFilesByNotification.value.get(notification.id) || [];
      const photos: ModalPhoto[] = [];
      for (const file of imageFiles) {
        const imageUrl = await fetchFileObjectUrl(file);
        photos.push({
          id: file.id,
          photoTitle: file.file_name,
          savedImage: imageUrl,
        });
      }

      return photos;
    };
    const closePhotoModal = (): void => {
      photoModalState.shouldShowPhotoModal = false;
      photoModalState.title = '';
      photoModalState.photos = [];
    };
    const preloadImages = async(): Promise<void> => {
      const allImageFiles = notifications.value
        .reduce<NotificationFile[]>((acc, n) => acc.concat(n.files || []), [])
        .filter(file => isImageFileType(file.file_path) && !state.imageUrls[file.id]);

      if (allImageFiles.length === 0) return;

      const batches = arrayChunk(allImageFiles, 8);
      for (const batch of batches) {
        const newFileUrls: Record<number, string> = {};
        await Promise.all(
          batch.map(async(file) => {
            try {
              const fileUrl = await fetchFileObjectUrl(file);
              if (fileUrl) {
                newFileUrls[file.id] = fileUrl;
              }
            } catch (error) {
              console.error('Failed to load image:', error);
            }
          }),
        );

        state.imageUrls = { ...state.imageUrls, ...newFileUrls };
      }
    };
    const getFirstPreviewImageUrl = (notification: Notification): string => {
      const imageFile = getFirstImageFile(notification);
      if (!imageFile) return '';

      return getFilePreviewUrl(imageFile);
    };
    const getFilePreviewUrl = (file: NotificationFile): string => {
      if (!file || !file.id) return '';

      return state.imageUrls[file.id] || '';
    };
    const getFirstImageFile = (notification: Notification): NotificationFile | null => {
      return imageFilesByNotification.value.get(notification.id)?.[0] || null;
    };
    const getImageFiles = (notificationId: number): NotificationFile[] => {
      return imageFilesByNotification.value.get(notificationId) || [];
    };
    const getNonImageFiles = (notificationId: number): NotificationFile[] => {
      return nonImageFilesByNotification.value.get(notificationId) || [];
    };
    const fetchFileObjectUrl = async(file: NotificationFile): Promise<string> => {
      if (!file || !file.id) return '';

      const fileObjInfo = await fetchFileAsObjectInfo(file.file_path);
      if (!fileObjInfo || !fileObjInfo.url) return '';

      return fileObjInfo.url;
    };

    onMounted(async() => {
      await fetchNotifications();
      await preloadImages();
      state.isOpen = shouldForceOpen();
      startAutoRefresh();
    });
    onUnmounted(() => {
      stopAutoRefresh();
      Object.values(state.imageUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      state.imageUrls = {};
    });

    return {
      state,
      photoModalState,
      notifications,
      // computed
      shouldShowArea,
      toggleButtonTitle,
      toggleButtonIcon,
      containerClasses,
      // methods
      toggleArea,
      viewNotificationDetails,
      formatDate,
      getFirstPreviewImageUrl,
      downloadFile,
      previewImageFile,
      fileTypeIconFromFilePath,
      closePhotoModal,
      getImageFileCount,
      getImageFiles,
      getNonImageFiles,
      closeDetailModal,
      getFilePreviewUrl,
      formatUrlsInText,
    };
  },
});
